import React from 'react';
import {
  FileText,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  DollarSign,
  Loader2,
  Eye,
  PauseCircle,
  PlayCircle,
  Star,
  ShieldCheck,
  Ban
} from 'lucide-react';

/**
 * StatusBadge - Componente para mostrar estados de forma consistente
 * 
 * @param {string} status - Estado a mostrar (ej: 'pendiente', 'borrador', 'finalizado')
 * @param {string} type - Tipo de entidad: 'ficha', 'cotizacion', 'cliente', 'comision', 'cuenta_cobro', 'prospecto'
 * @param {string} size - Tamaño: 'sm', 'md', 'lg'
 * @param {boolean} showIcon - Mostrar icono (default: true)
 * @param {string} customLabel - Label personalizado (opcional)
 */
export const StatusBadge = ({
  status,
  type = 'default',
  size = 'md',
  showIcon = true,
  customLabel,
  className = ''
}) => {
  // Configuración de estados por tipo de entidad
  const statusConfig = {
    // Estados de Fichas
    ficha: {
      borrador: { label: 'Borrador', color: 'bg-zinc-100 text-zinc-700 border-zinc-200', icon: FileText },
      en_proceso: { label: 'En Proceso', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
      finalizada: { label: 'Finalizada', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
      pendiente_medidas: { label: 'Sin Medidas', color: 'bg-orange-50 text-orange-700 border-orange-200', icon: AlertTriangle },
      entregada: { label: 'Entregada', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: ShieldCheck }
    },
    // Estados de Cotizaciones
    cotizacion: {
      borrador: { label: 'Borrador', color: 'bg-zinc-100 text-zinc-700 border-zinc-200', icon: FileText },
      enviada: { label: 'Enviada', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: Send },
      aprobada: { label: 'Aprobada', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
      rechazada: { label: 'Rechazada', color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
      vencida: { label: 'Vencida', color: 'bg-orange-50 text-orange-700 border-orange-200', icon: Clock },
      venta: { label: 'Venta', color: 'bg-green-100 text-green-800 border-green-300', icon: DollarSign },
      pendiente: { label: 'Pendiente', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
      seguimiento: { label: 'En Seguimiento', color: 'bg-purple-50 text-purple-700 border-purple-200', icon: Eye }
    },
    // Estados de Comisiones
    comision: {
      pendiente: { label: 'Pendiente', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
      aprobada: { label: 'Aprobada', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
      pagada: { label: 'Pagada', color: 'bg-green-100 text-green-800 border-green-300', icon: DollarSign },
      rechazada: { label: 'Rechazada', color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle }
    },
    // Estados de Cuenta de Cobro
    cuenta_cobro: {
      borrador: { label: 'Borrador', color: 'bg-zinc-100 text-zinc-700 border-zinc-200', icon: FileText },
      pendiente: { label: 'Pendiente', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
      procesando: { label: 'Procesando', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: Loader2 },
      pagada: { label: 'Pagada', color: 'bg-green-100 text-green-800 border-green-300', icon: DollarSign },
      rechazada: { label: 'Rechazada', color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle }
    },
    // Estados de Prospectos CRM
    prospecto: {
      'nuevo': { label: 'Nuevo', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: Star },
      'nuevo contacto': { label: 'Nuevo', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: Star },
      'contactado': { label: 'Contactado', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Send },
      'interesado': { label: 'Interesado', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Eye },
      'cita_agendada': { label: 'Cita Agendada', color: 'bg-purple-50 text-purple-700 border-purple-200', icon: Clock },
      'visita': { label: 'Visita', color: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: Eye },
      'cotizado': { label: 'Cotizado', color: 'bg-cyan-50 text-cyan-700 border-cyan-200', icon: FileText },
      'cerrado': { label: 'Cerrado', color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle },
      'descartado': { label: 'Descartado', color: 'bg-red-50 text-red-700 border-red-200', icon: Ban },
      'sin_respuesta': { label: 'Sin Respuesta', color: 'bg-zinc-100 text-zinc-600 border-zinc-200', icon: PauseCircle },
      'seguimiento': { label: 'En Seguimiento', color: 'bg-orange-50 text-orange-700 border-orange-200', icon: PlayCircle }
    },
    // Estados de Aliados
    aliado: {
      activo: { label: 'Activo', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
      inactivo: { label: 'Inactivo', color: 'bg-zinc-100 text-zinc-600 border-zinc-200', icon: PauseCircle },
      suspendido: { label: 'Suspendido', color: 'bg-red-50 text-red-700 border-red-200', icon: Ban }
    },
    // Estados de Usuario
    usuario: {
      activo: { label: 'Activo', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
      inactivo: { label: 'Inactivo', color: 'bg-zinc-100 text-zinc-600 border-zinc-200', icon: PauseCircle },
      pendiente: { label: 'Pendiente', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
      aprobado: { label: 'Aprobado', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: ShieldCheck }
    },
    // Default
    default: {
      pendiente: { label: 'Pendiente', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
      activo: { label: 'Activo', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
      inactivo: { label: 'Inactivo', color: 'bg-zinc-100 text-zinc-600 border-zinc-200', icon: PauseCircle },
      completado: { label: 'Completado', color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle },
      error: { label: 'Error', color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle }
    }
  };

  // Normalizar status (lowercase, sin acentos)
  const normalizedStatus = status?.toLowerCase()?.normalize("NFD")?.replace(/[\u0300-\u036f]/g, "") || 'pendiente';
  
  // Obtener configuración del estado
  const typeConfig = statusConfig[type] || statusConfig.default;
  const config = typeConfig[normalizedStatus] || typeConfig[status] || {
    label: customLabel || status || 'Desconocido',
    color: 'bg-zinc-100 text-zinc-600 border-zinc-200',
    icon: AlertTriangle
  };

  const Icon = config.icon;
  const label = customLabel || config.label;

  // Tamaños
  const sizes = {
    sm: 'px-2 py-0.5 text-[10px] gap-1',
    md: 'px-2.5 py-1 text-xs gap-1.5',
    lg: 'px-3 py-1.5 text-sm gap-2'
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
    lg: 'w-4 h-4'
  };

  return (
    <span
      className={`inline-flex items-center font-medium rounded-full border ${config.color} ${sizes[size]} ${className}`}
      data-testid={`status-badge-${normalizedStatus}`}
    >
      {showIcon && Icon && <Icon className={iconSizes[size]} />}
      <span>{label}</span>
    </span>
  );
};

export default StatusBadge;
